import { io } from 'socket.io-client';
import { getStoredTokenSync } from '../utils/authStorage';
import offlineManager from './offlineManager';
import { isPrintStationEnabled } from './remotePrintStation';
import {
	onSocketOrderCreated,
	onSocketOrderUpdated,
	onSocketPrintReport,
} from './remotePrintSocketHandler';

const getSocketUrl = () =>
	process.env.REACT_APP_SOCKET_URL ||
	process.env.REACT_APP_API_URL?.replace('/api', '') ||
	'http://localhost:5000';

class SocketService {
	constructor() {
		this.socket = null;
		this.isConnected = false;
		this.eventListeners = new Map();
		this.currentBranchId = null;

		// Automatically pause/resume the socket when the network goes offline/online
		// so repeated reconnection attempts don't cause POS flickering.
		if (typeof window !== 'undefined') {
			window.addEventListener('offline', () => this._handleOffline());
			window.addEventListener('online',  () => this._handleOnline());
		}
	}

	_handleOffline() {
		if (this.socket) {
			// Stop reconnection while offline to prevent rapid state-change re-renders
			this.socket.io.opts.reconnection = false;
			this.socket.disconnect();
		}
		this.isConnected = false;
		this.emit('socket_disconnected', { reason: 'network_offline' });
	}

	_handleOnline() {
		if (this.currentBranchId) {
			// Small delay so the network is stable before reconnecting
			setTimeout(() => this.connect(this.currentBranchId), 1500);
		}
	}

	connect(branchId = null) {
		// Don't try to connect when there is no internet — it only causes
		// rapid reconnect/disconnect cycles that make the POS UI flicker.
		if (!navigator.onLine) {
			this.currentBranchId = branchId; // remember it for when we come back online
			return;
		}

		if (this.socket?.connected && this.currentBranchId === branchId) return;
		if (this.socket) this.disconnect();

		this.currentBranchId = branchId;
		const serverUrl = getSocketUrl();
		this.socket = io(serverUrl, {
			autoConnect: true,
			reconnection: true,
			reconnectionDelay: 3000,      // wait 3 s between attempts
			reconnectionDelayMax: 30000,  // cap at 30 s (allows server cold start)
			reconnectionAttempts: 20,     // keep trying longer after server restart
			timeout: 20000,
			transports: ['websocket', 'polling'],
			auth: {
				token: getStoredTokenSync(),
				branchId,
			},
		});
		this.setupEventHandlers();
	}

	setupEventHandlers() {
		if (!this.socket) return;

		// Prevent duplicate handlers if connect() is invoked again on the same socket.
		this.socket.off('order_created');
		this.socket.off('order_updated');
		this.socket.off('order_deleted');
		this.socket.off('print_report');
		this.socket.off('order_status_changed');
		this.socket.off('table_status_changed');
		this.socket.off('sync_orders');
		this.socket.off('sync_tables');
		this.socket.off('session:started');
		this.socket.off('session:ended');
		this.socket.off('session:day_closed');
		this.socket.off('online_order_pending');
		this.socket.off('online_order_resolved');

		this.socket.on('connect', () => {
			this.isConnected = true;
			if (this.currentBranchId) {
				this.socket.emit('join_branch', { branchId: this.currentBranchId });
				this.registerPrintStation(this.currentBranchId);
			}
			this.emit('socket_connected', { socketId: this.socket.id });
		});

		this.socket.on('disconnect', (reason) => {
			this.isConnected = false;
			this.emit('socket_disconnected', { reason });
		});

		this.socket.on('connect_error', (error) => {
			this.emit('socket_error', { error: error?.message ?? 'Connection failed' });
		});

		this.socket.on('order_created', (data) => {
			this.emit('order_created', data);
			onSocketOrderCreated(data).catch((err) => {
				console.error('[print-station] order_created handler error:', err);
			});
		});
		this.socket.on('order_updated', (data) => {
			this.emit('order_updated', data);
			onSocketOrderUpdated(data).catch((err) => {
				console.error('[print-station] order_updated handler error:', err);
			});
		});
		this.socket.on('order_deleted', (data) => this.emit('order_deleted', data));
		this.socket.on('print_report', (data) => {
			this.emit('print_report', data);
			onSocketPrintReport(data).catch((err) => {
				console.error('[print-station] print_report handler error:', err);
			});
		});
		this.socket.on('order_status_changed', (data) => this.emit('order_status_changed', data));
		this.socket.on('table_status_changed', (data) => this.emit('table_status_changed', data));
		this.socket.on('sync_orders', (data) => this.emit('sync_orders', data));
		this.socket.on('sync_tables', (data) => this.emit('sync_tables', data));

		// Session events — broadcast by branchSessionController so every POS device
		// on this branch immediately updates its session state without polling.
		this.socket.on('session:started',    (data) => this.emit('session:started', data));
		this.socket.on('session:ended',      (data) => this.emit('session:ended', data));
		this.socket.on('session:day_closed', (data) => this.emit('session:day_closed', data));

		// Online orders (QR menu / WhatsApp) awaiting POS approval.
		this.socket.on('online_order_pending',  (data) => this.emit('online_order_pending', data));
		this.socket.on('online_order_resolved', (data) => this.emit('online_order_resolved', data));
	}

	registerPrintStation(branchId = null) {
		if (!this.socket?.connected) return;
		if (typeof window === 'undefined' || !window.electronAPI?.isElectron) return;
		const bid = branchId ?? this.currentBranchId;
		if (!bid) return;
		this.socket.emit('register_print_station', {
			branchId: bid,
			posMode: offlineManager.getMode(),
			isElectron: true,
			printStationEnabled: isPrintStationEnabled(),
		});
	}

	joinBranchRoom(branchId) {
		if (!this.socket?.connected) return;
		this.socket.emit('join_branch', { branchId });
		this.currentBranchId = branchId;
		this.registerPrintStation(branchId);
	}

	leaveBranchRoom(branchId) {
		if (!this.socket?.connected) return;
		this.socket.emit('leave_branch', { branchId });
		if (this.currentBranchId === branchId) this.currentBranchId = null;
	}

	requestSync(branchId = null) {
		if (!this.socket?.connected) return;
		this.socket.emit('request_sync', { branchId: branchId ?? this.currentBranchId });
	}

	/**
	 * Emits a print_report event to the backend, which relays it to all
	 * other connected print stations in the same branch.
	 */
	emitPrintReport(branchId, reportText, opts = {}) {
		if (!this.socket?.connected) return;
		this.socket.emit('print_report', {
			branchId,
			reportText,
			logoUrl: opts.logoUrl || null,
		});
	}

	addEventListener(event, callback) {
		if (!this.eventListeners.has(event)) this.eventListeners.set(event, []);
		this.eventListeners.get(event).push(callback);
	}

	removeEventListener(event, callback) {
		if (!this.eventListeners.has(event)) return;
		const list = this.eventListeners.get(event);
		const i = list.indexOf(callback);
		if (i > -1) list.splice(i, 1);
	}

	emit(event, data) {
		const list = this.eventListeners.get(event);
		if (!list?.length) return;
		for (const cb of list) {
			try {
				cb(data);
			} catch (err) {
				// no-op to avoid breaking other listeners
			}
		}
	}

	isSocketConnected() {
		return this.socket != null && this.isConnected;
	}

	getSocketId() {
		return this.socket?.id ?? null;
	}

	disconnect() {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
		}
		this.isConnected = false;
		this.currentBranchId = null;
	}

	cleanup() {
		this.eventListeners.clear();
		this.disconnect();
	}
}

const socketService = new SocketService();
export default socketService;
